// Modal that previews + lets the owner share their RATING STATS as an
// image. Sibling of ShareCardModal (which shares one rating's note);
// same Canvas-rasterise + Web Share / Download / Copy SVG flow, just a
// different backend endpoint scoped to a time period.
//
// Why a separate component vs a flag on ShareCardModal: the input shape
// is different (period vs ratingId), the endpoint differs, and the
// filename suffix needs the period. Two small components stay easier
// to read than one branched one.

import { useEffect, useMemo, useState } from 'react';
import { sessionStore, type Period } from '@/lib/api';
import { friendlyError } from '@/lib/errors';

const API_BASE = import.meta.env.VITE_API_BASE ?? 'http://localhost:8080';

interface Props {
  /** Time window to render stats for. Re-fetches when this changes. */
  period: Period;
  /** Used in the download filename. */
  slug: string;
  onClose: () => void;
}

export function ShareStatsCardModal({ period, slug, onClose }: Props) {
  const [svg, setSvg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<'png' | 'copy' | 'share' | null>(null);

  // Same detection as ShareCardModal — Web Share API with files is
  // mobile-first; desktop browsers fail the canShare probe + see the
  // Download button.
  const canNativeShare = (() => {
    if (typeof navigator === 'undefined') return false;
    const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
    if (!nav.canShare) return false;
    try {
      const probe = new File([new Blob(['x'], { type: 'image/png' })], 'probe.png', { type: 'image/png' });
      return nav.canShare({ files: [probe] });
    } catch {
      return false;
    }
  })();

  // Fetch the SVG on mount + whenever period changes. The endpoint is
  // owner-only so we have to use fetch (Authorization header) rather
  // than rendering <img src=…>.
  useEffect(() => {
    let cancelled = false;
    setSvg(null);
    setErr(null);
    const token = sessionStore.getAccess();
    fetch(`${API_BASE}/profiles/me/stats/share.svg?period=${period}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
        return r.text();
      })
      .then((text) => { if (!cancelled) setSvg(text); })
      .catch((e) => { if (!cancelled) setErr(String(e)); });
    return () => { cancelled = true; };
  }, [period]);

  const svgURL = useMemo(() => {
    if (!svg) return null;
    const blob = new Blob([svg], { type: 'image/svg+xml' });
    return URL.createObjectURL(blob);
  }, [svg]);
  useEffect(() => () => { if (svgURL) URL.revokeObjectURL(svgURL); }, [svgURL]);

  const filename = `wydm-${slug || 'stats'}-stats-${period}.png`;

  // Rasterize the loaded SVG to a PNG Blob at 1080×1080. Mirrors
  // ShareCardModal.rasterizeToBlob — wait on document.fonts.ready
  // before drawImage so the brand typefaces are loaded.
  const rasterizeToBlob = async (): Promise<Blob> => {
    if (!svgURL) throw new Error('SVG not loaded.');
    try { await (document as any).fonts?.ready; } catch { /* ignore */ }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    const loaded = new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Could not load SVG into <img> for rasterization.'));
    });
    img.src = svgURL;
    await loaded;

    const [w, h] = [1080, 1080];
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable.');
    ctx.drawImage(img, 0, 0, w, h);

    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) reject(new Error('Could not encode PNG.'));
        else resolve(blob);
      }, 'image/png');
    });
  };

  const downloadPNG = async () => {
    setBusy('png');
    try {
      const blob = await rasterizeToBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setErr(friendlyError(e, 'ShareStatsCardModal'));
    } finally {
      setBusy(null);
    }
  };

  const shareImage = async () => {
    setBusy('share');
    try {
      const blob = await rasterizeToBlob();
      const file = new File([blob], filename, { type: 'image/png' });
      const nav = navigator as Navigator & { share?: (data: ShareData) => Promise<void> };
      if (!nav.share) throw new Error('Sharing not available.');
      await nav.share({
        files: [file],
        title: 'My WYDM stats',
        text: 'My rating stats from WYDM.',
      });
    } catch (e) {
      const name = (e as { name?: string }).name;
      if (name !== 'AbortError') setErr(friendlyError(e, 'ShareStatsCardModal'));
    } finally {
      setBusy(null);
    }
  };

  const copySVGUrl = async () => {
    setBusy('copy');
    try {
      if (svg) await navigator.clipboard.writeText(svg);
    } finally {
      setBusy(null);
      setTimeout(() => setBusy(null), 500);
    }
  };

  return (
    <div style={s.scrim} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <div style={s.header}>
          <div className="mono" style={s.eyebrow}>STATS CARD · {labelFor(period).toUpperCase()}</div>
          <button type="button" onClick={onClose} style={s.closeBtn} aria-label="Close">×</button>
        </div>

        <div
          style={{
            ...s.previewBox,
            aspectRatio: '1 / 1',
            maxWidth: 380,
          }}
        >
          {err ? (
            <div style={s.errorBox}>{err}</div>
          ) : svgURL ? (
            <img src={svgURL} alt="Stats share preview" style={s.previewImg} />
          ) : (
            <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>Loading…</div>
          )}
        </div>

        <div style={s.actionsRow}>
          {canNativeShare ? (
            <button
              type="button"
              onClick={shareImage}
              disabled={!svg || busy === 'share'}
              style={s.primaryBtn}
            >
              {busy === 'share' ? 'Opening…' : 'Share'}
            </button>
          ) : (
            <button
              type="button"
              onClick={downloadPNG}
              disabled={!svg || busy === 'png'}
              style={s.primaryBtn}
            >
              {busy === 'png' ? 'Exporting…' : 'Download PNG'}
            </button>
          )}
          <button
            type="button"
            onClick={copySVGUrl}
            disabled={!svg || busy === 'copy'}
            style={s.secondaryBtn}
          >
            {busy === 'copy' ? 'Copied' : 'Copy SVG'}
          </button>
        </div>
      </div>
    </div>
  );
}

function labelFor(p: Period): string {
  if (p === 'today') return 'Today';
  if (p === 'week') return 'This week';
  return 'All time';
}

// ── styles ─────────────────────────────────────────────────────────────────
// Identical to ShareCardModal — keep the two modals visually aligned.

const s = {
  scrim: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(43, 29, 20, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  modal: {
    background: 'var(--bg)',
    borderRadius: 22,
    padding: 24,
    width: '100%',
    maxWidth: 460,
    boxShadow: 'var(--shadow-lift)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  eyebrow: {
    fontSize: 11, color: 'var(--coral)', letterSpacing: 2, textTransform: 'uppercase' as const,
  },
  closeBtn: {
    width: 32, height: 32, borderRadius: 32,
    background: 'var(--surface)', color: 'var(--ink)', border: 'none',
    fontSize: 22, lineHeight: 1, cursor: 'pointer',
  },
  previewBox: {
    margin: '0 auto 20px',
    background: 'var(--surface-2)',
    borderRadius: 14,
    overflow: 'hidden' as const,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  previewImg: { width: '100%', height: '100%', display: 'block', objectFit: 'cover' as const },
  errorBox: { color: '#a8331f', fontSize: 12, padding: 14, textAlign: 'center' as const },
  actionsRow: { display: 'flex', gap: 8 },
  primaryBtn: {
    flex: 1,
    padding: '12px 20px',
    background: 'var(--coral)', color: '#fff',
    border: 'none', borderRadius: 100,
    fontWeight: 600, fontSize: 14, cursor: 'pointer',
    fontFamily: 'var(--font-ui)',
  },
  secondaryBtn: {
    flex: 1,
    padding: '12px 20px',
    background: 'transparent', color: 'var(--ink)',
    border: '1.5px solid var(--line-strong)', borderRadius: 100,
    fontWeight: 600, fontSize: 14, cursor: 'pointer',
    fontFamily: 'var(--font-ui)',
  },
};
